import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl } from '@angular/forms';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { EntiteModelExtended } from 'src/app/models/entites/entiteModelExtended.model';
import { EntiteModel } from '../../../models/entites/entite.model';
import { EntitesService } from '../../../services/entites/entites.service';

@Component({
  selector: 'app-entite-selector',
  templateUrl: './entite-selector.component.html',
  styleUrls: ['./entite-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EntiteSelectorComponent),
      multi: true,
    },
  ],
})
export class EntiteSelectorComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() set idEntites(value: number[]) {
    this._idEntites = value;
  }
  @Input() hideIfSingleEntite = true;
  @Input() set filter(value: number[]) {
    this._filter = value;
    this.defineEntitesVisibles();
  }
  @Input() doRefresh = new Observable<{idEntites: number[], idEntitesFilters: number[]}>();
  formControl: UntypedFormControl = new UntypedFormControl();
  selected: number;
  private entites: EntiteModelExtended[];
  private allEntites: EntiteModelExtended[];
  private visible = false;
  entitesVisibles: EntiteModelExtended[];
  private _idEntites: number[];
  private _filter: number[];
  private subs: Subscription[] = [];
  isSingleEntite = false;

  constructor(private entitesService: EntitesService) {
    this.formControl.valueChanges.subscribe(newValue => {
      this.writeValue(newValue);
    });
  }

  ngOnInit(): void {
    this.loadEntites();
    this.subs.push(
      this.doRefresh.subscribe((elt: {idEntites: number[], idEntitesFilters: number[]}) => {
        this.idEntites = elt.idEntites;
        this.filter = elt.idEntitesFilters;
        this.loadEntites();
      }),
    );
  }

  ngOnDestroy() {
    this.subs.forEach(it => it.unsubscribe());
    this.subs = [];
  }

  get idEntites(): number[] {
    return this._idEntites;
  }

  get filter(): number[] {
    return this._filter;
  }

  private makeEditable(editable: boolean = true) {
    return (entite: EntiteModel): EntiteModelExtended =>
      new EntiteModelExtended(entite.id, entite.code, entite.libelle, entite.active, editable);
  }

  private defineEntitesVisibles() {
    if (this.entites != null) {
      this.entitesVisibles = [...this.entites];
    } else {
      this.entitesVisibles = [];
    }
    if (this.filter != null) {
      this.entitesVisibles = this.applyEntiteFilter(this.entitesVisibles);
    }
    if (this.selected != null && this.entitesVisibles.findIndex(e => e.id == this.selected) < 0) {
      this.addEntiteToListeIfExists(this.selected);
    }
    this.updateEnableDisable();
  }

  private updateEnableDisable() {
    const entiteSelected = this.getEntiteById(this.selected);
    if (entiteSelected != null && entiteSelected.editable == false) {
      this.formControl.disable();
    } else {
      this.formControl.enable();
    }
  }

  private applyEntiteFilter(entites: EntiteModelExtended[]) {
    return entites.filter(e => this.filter.indexOf(e.id) >= 0);
  }

  private loadEntites() {
    if (typeof this.idEntites == 'undefined' || this.idEntites == null) {
      combineLatest([
        this.entitesService.getUtilisateursEntites().pipe(map(entites => entites.map(this.makeEditable()))),
        this.getAllEntites(),
      ]).subscribe(([entitesUtilisateur, allEntites]) => {
        this.entites = entitesUtilisateur;
        this.allEntites = allEntites;
        this.defineEntitesVisibles();
      });
    }
    else {
      this.getAllEntites().subscribe(allEntites => {
        this.entites = allEntites.filter(e => this.idEntites.includes(e.id));
        this.entites.forEach(e => e.editable = true);
        this.allEntites = allEntites;
        this.defineEntitesVisibles();
      });
    }
  }

  private getAllEntites():Observable<EntiteModelExtended[]> {
    return this.entitesService.getUtilisateursEntites().pipe(
      map(entites => entites.map(this.makeEditable(false))),
      tap(e => {
        if (this.hideIfSingleEntite) {
          this.visible = e.length > 1;
          this.isSingleEntite = !this.visible;
        }
      }),
    );
  }

  private getEntiteById(id: number) {
    let entite = null;
    if (this.entites != null) {
      entite = this.entites.find(e => e.id == id);
    }
    if (entite == null && this.allEntites != null) {
      return this.allEntites.find(it => it.id == id);
    }
    return entite;
  }

  get isHidden() {
    return this.visible == false;
  }

  onChange: (value) => any = v => {};

  registerOnChange(fn: (value) => any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => any): void {}

  private entiteExists(id: number) {
    return this.allEntites != null && this.allEntites.findIndex(it => it.id == id) >= 0;
  }

  private isEntiteActuellementProposeeInSelector(id: number) {
    return this.entitesVisibles != null && this.entitesVisibles.findIndex(it => it.id == id) >= 0;
  }

  writeValue(id: number): void {
    if (this.selected != id) {
      this.selected = id;
      this.onChange(id);
      this.formControl.reset(id);
      this.defineEntitesVisibles();
    }
  }

  private addEntiteToListeIfExists(id: number) {
    if (!this.isEntiteActuellementProposeeInSelector(id) && this.entiteExists(id)) {
      this.entitesVisibles.push(this.getEntiteById(id));
    }
  }
}
